import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ClipLoader } from "react-spinners";
import { createIndustryByAdmin } from "../../services/industriesService";

const industrySchema = z.object({
  name: z.string().min(2, "Industry name required."),
});

interface AddIndustryModalProps {
  onClose: () => void;
}

const AddIndustryModal = ({ onClose }: AddIndustryModalProps) => {
  const queryClient = useQueryClient();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(industrySchema),
    defaultValues: {
      name: "",
    },
  });

  const mutation = useMutation({
    mutationFn: createIndustryByAdmin,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["industries"] });
      onClose();
    },
  });

  const onSubmit = (data: { name: string }) => {
    mutation.mutate(data);
  };

  return (
    <div className="fixed inset-0 p-2 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white p-6 rounded-lg shadow-lg w-96">
        <h2 className="text-xl mb-4">New Industry</h2>
        <form onSubmit={handleSubmit(onSubmit)}>
          <label className="block mb-2 text-sm font-medium text-gray-700">
            Industry Name
            <input
              {...register("name")}
              type="text"
              className="w-full mt-1 p-2 border rounded-md"
            />
            {errors.name && (
              <p className="text-sm text-rose-500 mt-1">
                {errors.name.message}
              </p>
            )}
          </label>

          <div className="flex justify-end mt-4">
            <button
              type="button"
              className="px-4 py-2 text-rose-500 mt-1 rounded-lg text-sm"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-sm rounded-lg border flex items-center justify-center"
              disabled={mutation.isPending}
            >
              {mutation.isPending ? <ClipLoader size={20} /> : "Add Industry"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddIndustryModal;
